import type {
  FieldOpsQueueOwner,
  FieldOpsQueueOwnerInput,
  FieldOpsQueueOwnerPatch,
  FieldOpsTeam,
  QueueOwnerResolution,
  ResolvedQueueOwner,
} from '../domain/fieldOpsTeam';
import { fieldOpsQueueOwnerSchema } from '../domain/validation';
import { ApiError, ConflictError, ForbiddenError, NotFoundError } from './errors';
import { withoutUndefined } from './patch';
import type { FieldOpsQueueOwnersApi } from './field-ops-queue-owners-api';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api';

function redirectToSignIn(): void {
  const returnTo = `${window.location.pathname}${window.location.search}`;
  window.location.assign(`/auth/google?returnTo=${encodeURIComponent(returnTo)}`);
}

export class LiveFieldOpsQueueOwnersApi implements FieldOpsQueueOwnersApi {
  private async fetchJSON(path: string, init?: RequestInit): Promise<Record<string, unknown>> {
    let response: Response;
    try {
      response = await fetch(`${BASE_URL}${path}`, {
        ...init,
        headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
      });
    } catch (error) {
      throw new ApiError(
        error instanceof Error ? error.message : 'Network request failed',
        'NETWORK',
      );
    }
    const body = (await response.json().catch(() => ({}))) as Record<string, unknown>;
    if (!response.ok) {
      if (response.status === 401) {
        redirectToSignIn();
        throw new ApiError('Your session expired. Redirecting to sign-in…', 'UNAUTHENTICATED');
      }
      const message =
        typeof body.error === 'string'
          ? body.error
          : typeof body.message === 'string'
            ? body.message
            : `Request failed with status ${response.status}`;
      const code = body.code === 5 ? 'NOT_FOUND' : body.code;
      if (code === 'NOT_FOUND' || /not found/i.test(message)) throw new NotFoundError(message);
      if (/version .* does not match/i.test(message)) throw new ConflictError(message);
      if (/not authorized/i.test(message)) throw new ForbiddenError(message);
      throw new ApiError(message, 'INTERNAL');
    }
    return body;
  }

  async listQueueOwners(includeInactive = false): Promise<FieldOpsQueueOwner[]> {
    const params = new URLSearchParams();
    if (includeInactive) params.set('includeInactive', 'true');
    const raw = await this.fetchJSON(`/v1/fieldOpsQueueOwners?${params.toString()}`);
    const rows = Array.isArray(raw.fieldOpsQueueOwners) ? raw.fieldOpsQueueOwners : [];
    return rows.map((row) => fieldOpsQueueOwnerSchema.parse(row));
  }

  async createQueueOwner(input: FieldOpsQueueOwnerInput): Promise<FieldOpsQueueOwner> {
    const raw = await this.fetchJSON('/v1/fieldOpsQueueOwners', {
      method: 'POST',
      body: JSON.stringify(withoutUndefined(input)),
    });
    return fieldOpsQueueOwnerSchema.parse(raw);
  }

  async updateQueueOwner(patch: FieldOpsQueueOwnerPatch): Promise<FieldOpsQueueOwner> {
    const { id, ...rest } = patch;
    const raw = await this.fetchJSON(`/v1/fieldOpsQueueOwners/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(withoutUndefined(rest)),
    });
    return fieldOpsQueueOwnerSchema.parse(raw);
  }

  async resolveQueueOwner(team: FieldOpsTeam): Promise<ResolvedQueueOwner> {
    const params = new URLSearchParams({ team });
    const raw = await this.fetchJSON(`/v1/fieldOpsQueueOwners:resolve?${params.toString()}`);
    return {
      owner: fieldOpsQueueOwnerSchema.parse(raw.fieldOpsQueueOwner),
      resolution: (raw.resolution as QueueOwnerResolution) ?? 'REGIONAL',
    };
  }
}

export const fieldOpsQueueOwnersApi: FieldOpsQueueOwnersApi = new LiveFieldOpsQueueOwnersApi();
